<script setup lang="ts">
import { onMounted, onUnmounted, ref } from 'vue'
import BaseButton from './base-button.vue'

interface Props {
  message: string
  duration?: number
  type?: 'success' | 'error' | 'info'
  isVisible: boolean
  buttons?: {
    text: string
    variant?: 'primary' | 'secondary' | 'outline' | 'ghost' | 'danger'
    onClick: () => void
    isDisabled?: boolean
  }[]
  showClose?: boolean
  icon?: {
    content: string
    background?: string
  }
}

const props = withDefaults(defineProps<Props>(), {
  duration: 3000,
  type: 'success',
  isVisible: false,
  buttons: () => [],
  showClose: true,
  icon: undefined,
})

const emit = defineEmits<{
  (e: 'close'): void
}>()

const timeoutId = ref<number | null>(null)

onMounted(() => {
  if (props.duration > 0 && !props.buttons.length) {
    timeoutId.value = setTimeout(() => {
      emit('close')
    }, props.duration)
  }
})

onUnmounted(() => {
  if (timeoutId.value) {
    clearTimeout(timeoutId.value)
  }
})
</script>

<template>
  <Transition name="toast">
    <div
      v-if="isVisible"
      class="toast-container"
      role="alert"
      aria-live="polite"
      aria-atomic="true"
    >
      <div class="toast" :class="[type, { 'with-buttons': buttons.length }]">
        <div class="toast-content">
          <span v-if="icon" class="icon" :style="{ background: icon.background }">{{
            icon.content
          }}</span>
          <span class="message">{{ message }}</span>
        </div>

        <div v-if="buttons.length" class="button-container">
          <BaseButton
            v-for="(button, index) in buttons"
            :key="index"
            :variant="button.variant"
            :disabled="button.isDisabled"
            @click="button.onClick"
          >
            {{ button.text }}
          </BaseButton>
        </div>

        <button v-if="showClose && type !== 'success'" class="close-button" @click="emit('close')">
          <span class="close-icon">×</span>
        </button>
      </div>
    </div>
  </Transition>
</template>

<style lang="scss" scoped>
.toast-container {
  position: fixed;
  bottom: 50px;
  left: 50%;
  z-index: 10000;
  transform: translateX(-50%);
}

.toast-enter-active,
.toast-leave-active {
  transition: opacity 0.3s ease;
}

.toast-enter-from,
.toast-leave-to {
  opacity: 0;
}

.toast-enter-to,
.toast-leave-from {
  opacity: 1;
}

.toast {
  display: flex;
  width: 499px;
  padding: var(--Spacing-2, 8px) var(--Spacing-2, 8px) var(--Spacing-2, 8px) var(--Spacing-5, 24px);
  justify-content: space-between;
  align-items: center;
  background: var(--Colors-Gray-12, #202020);
  color: white;
  border-radius: 32px;
  box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.1);
}

/* Type-specific styling */
.toast.success {
  background: var(--Colors-Gray-12, #202020);
}

.toast.error {
  background: #ef4444;
}

.toast.info {
  background: #3b82f6;
}

.toast.with-buttons {
  padding: 16px;
  border-radius: 36px;
}

.toast-content {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
}

.icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  font-size: 14px;
  flex-shrink: 0;
  color: white;
}

.message {
  font-family: var(--font-family);
  font-size: 14px;
  font-weight: 400;
}

.button-container {
  display: flex;
  gap: 8px;
  margin-left: auto;
}

.close-button {
  background: none;
  border: none;
  color: white;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: background-color 0.2s;
}

.close-button:hover {
  background-color: rgba(255, 255, 255, 0.1);
}

.close-icon {
  font-size: 18px;
  line-height: 1;
}
</style>
